import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.jsx'
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "./firebase";

const LOGIN_PATH_RE = /^\/(login|signin|auth)/i;
const SEARCH_AUTH_RE = /(?:^|[?&])(auth=|oauth_state_id=)/; // treat these as auth flow
const SEARCH_LOGOUT_RE = /(?:^|[?&])logout=1(?:&|$)/;

// --- add this line near the imports ---
const EARLY_IDLE_GUARD_ENABLED = false;

const inAuthFlow = () => {
  try {
    if (sessionStorage.getItem("trelli:authFlow") === "1") return true;
  } catch {}
  if (LOGIN_PATH_RE.test(location.pathname)) return true;
  if (SEARCH_AUTH_RE.test(location.search)) return true;
  return false;
};

// Read both per-user and global deadlines (works whether App wrote per-user or global)
const idleKeyFor = (uid) => `trelli:idleDeadline:${uid || "anon"}`;
const GLOBAL_IDLE_KEY = "trelli:idleDeadline:global";
function getAnyDeadline(uid) {
  try {
    const g = localStorage.getItem(GLOBAL_IDLE_KEY);
    const p = uid ? localStorage.getItem(idleKeyFor(uid)) : null;
    const gNum = g ? Number(g) : null;
    const pNum = p ? Number(p) : null;
    return Math.max(gNum || 0, pNum || 0) || null;
  } catch { return null; }
}

function clearGlobalDeadline() {
  try { localStorage.removeItem(GLOBAL_IDLE_KEY); } catch {}
}

// Early auth observer: if stored deadline already passed, sign out before rendering
onAuthStateChanged(auth, (user) => {
  if (!EARLY_IDLE_GUARD_ENABLED) return;
  if (!user) return;
  if (inAuthFlow()) return;              // don't kick during login/MFA/OAuth
  if (SEARCH_LOGOUT_RE.test(location.search)) return; // already on logout hop
  const dl = getAnyDeadline(user.uid);
  if (dl && Date.now() > dl) {
    clearGlobalDeadline();               // break any loop
    signOut(auth).finally(() => { window.location.replace("/?logout=1"); });
  }
});

// bfcache restore (iOS/Chrome mobile): recheck on every pageshow
window.addEventListener("pageshow", () => {
  if (!EARLY_IDLE_GUARD_ENABLED) return;
  if (inAuthFlow()) return;
  if (SEARCH_LOGOUT_RE.test(location.search)) return;
  const u = auth.currentUser;
  if (!u) return;
  const dl = getAnyDeadline(u.uid);
  if (dl && Date.now() > dl) {
    clearGlobalDeadline();
    signOut(auth).finally(() => { window.location.replace("/?logout=1"); });
  }
});

console.log("BOOT: main.jsx about to render App");

// hard-enforce sign out if an old deadline already passed (handles cold boot)
// but DO NOT set the forceSignOut flag while we are in an auth/MFA flow
try {
  const inAuth = inAuthFlow(); // uses sessionStorage flag + URL hints
  const uid = localStorage.getItem("trelli:lastUid") || "anon";
  const key = `trelli:idleDeadline:${uid}`;
  const raw = localStorage.getItem(key);
  const expired = raw && Date.now() > Number(raw);

  if (expired && !inAuth) {
    sessionStorage.setItem("trelli:forceSignOut", "1");
  } else {
    // if we’re in auth flow (or not expired), make sure there isn’t a stale flag
    sessionStorage.removeItem("trelli:forceSignOut");
  }
} catch {}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <App />
  </StrictMode>,
)